import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Form, Input, Button, Card, Result, Row, Col, Progress } from 'antd';
import {
  LockOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useResetPasswordMutation } from '../../store/slices/authSlice';
import { VersionInfo } from '../../utils/version';
import { CONTACT_CONFIG } from '../../config/contact';

// 密码强度检查
const checkPasswordStrength = (password: string) => {
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };

  const passedCount = Object.values(checks).filter(Boolean).length;
  const strength = passedCount === 5 ? 100 : passedCount * 20;

  return { checks, strength, passedCount };
};

const ResetPasswordPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [form] = Form.useForm();
  const [resetPassword, { isLoading }] = useResetPasswordMutation();
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [password, setPassword] = useState('');

  const { checks, strength } = checkPasswordStrength(password);
  const resolveLocalizedMessage = (
    backendMessage: string | undefined,
    fallback: string
  ) => {
    if (!backendMessage) return fallback;
    const isZh = i18n.language.toLowerCase().startsWith('zh');
    const hasChinese = /[\u4e00-\u9fff]/.test(backendMessage);
    return !isZh && hasChinese ? fallback : backendMessage;
  };

  // 检查token是否存在
  useEffect(() => {
    if (!token) {
      setErrorMessage(t('auth:resetPassword.linkInvalid'));
    }
  }, [token, t]);

  const handleSubmit = async (values: {
    password: string;
    confirmPassword: string;
  }) => {
    if (!token) {
      setErrorMessage(t('auth:resetPassword.linkInvalid'));
      return;
    }

    setErrorMessage('');

    try {
      const result = await resetPassword({
        token,
        password: values.password,
        confirmPassword: values.confirmPassword,
      }).unwrap();

      if (result.success) {
        setSuccess(true);
      } else {
        setErrorMessage(
          resolveLocalizedMessage(result.message, t('auth:resetPassword.failed'))
        );
      }
    } catch (error: any) {
      console.error('Reset password error:', error);
      const errorMsg =
        error?.data?.error?.message ||
        error?.data?.message ||
        t('auth:resetPassword.failed');
      setErrorMessage(
        resolveLocalizedMessage(errorMsg, t('auth:resetPassword.failed'))
      );
    }
  };

  const getStrengthColor = () => {
    if (strength <= 40) return '#ff4d4f';
    if (strength <= 60) return '#faad14';
    if (strength <= 80) return '#52c41a';
    return '#52c41a';
  };

  const getStrengthText = () => {
    if (strength <= 40) return t('auth:resetPassword.strength.weak');
    if (strength <= 60) return t('auth:resetPassword.strength.medium');
    if (strength <= 80) return t('auth:resetPassword.strength.strong');
    return t('auth:resetPassword.strength.veryStrong');
  };

  return (
    <Row className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      {/* 左侧品牌展示区域 */}
      <Col
        xs={0}
        sm={0}
        md={0}
        lg={14}
        xl={16}
        style={{
          minHeight: '100vh',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-600"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-white/5"></div>

        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-white/15 rounded-full blur-3xl"></div>
          <div className="absolute inset-0 opacity-5">
            <div
              className="w-full h-full"
              style={{
                backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
        </div>

        <div
          className="relative z-10 text-white max-w-2xl"
          style={{ padding: '0 4rem', width: '100%' }}
        >
          <div className="mb-16">
            <div className="flex items-center mb-8">
              <div className="w-16 h-16 bg-white/25 backdrop-blur-sm rounded-2xl flex items-center justify-center mr-5 border border-white/40 shadow-lg">
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm">
                  <span className="text-2xl">🌱</span>
                </div>
              </div>
              <div>
                <h1 className="text-3xl font-bold tracking-tight drop-shadow-sm">
                  {t('auth:brand.name')}
                </h1>
                <p className="text-white/85 text-base font-light">
                  Offshore Green Bond Express
                </p>
              </div>
            </div>
          </div>

          <div className="mb-16">
            <div className="space-y-4">
              <h2 className="text-3xl xl:text-4xl font-bold leading-tight tracking-tight drop-shadow-sm">
                {t('auth:resetPassword.leftTitlePrefix')}
                <br />
                <span className="bg-gradient-to-r from-white via-emerald-100 to-emerald-200 bg-clip-text text-transparent">
                  {t('auth:resetPassword.leftTitleHighlight')}
                </span>
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-white via-white/80 to-white/50 rounded-full shadow-sm"></div>
            </div>
            <p className="text-lg text-white/90 leading-relaxed mt-8 max-w-lg">
              {t('auth:resetPassword.leftDescription')}
            </p>
          </div>
        </div>
      </Col>

      {/* 右侧表单区域 */}
      <Col
        xs={24}
        sm={24}
        md={24}
        lg={10}
        xl={8}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1.5rem',
          minHeight: '100vh',
        }}
      >
        <div className="w-full max-w-md">
          <Card
            className="shadow-xl border-0 overflow-hidden"
            style={{ borderRadius: '16px' }}
            styles={{ body: { padding: 0 } }}
          >
            {/* 卡片头部 */}
            <div
              className="px-8 pt-6 pb-4"
              style={{
                background:
                  'linear-gradient(180deg, rgba(16, 185, 129, 0.08) 0%, rgba(255,255,255,0) 100%)',
              }}
            >
              <h2 className="text-xl font-bold text-gray-800 mb-1">
                {t('auth:resetPassword.title')}
              </h2>
              <p className="text-gray-500 text-sm">
                {t('auth:resetPassword.subtitle')}
              </p>
            </div>

            {/* 表单区域 */}
            <div className="px-8 py-6 bg-gradient-to-b from-white to-gray-50/30">
              {success ? (
                <Result
                  status="success"
                  title={t('auth:resetPassword.success')}
                  subTitle={t('auth:resetPassword.successMessage')}
                  extra={[
                    <Link to="/auth/login" key="login">
                      <Button
                        type="primary"
                        size="large"
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        {t('auth:activate.login')}
                      </Button>
                    </Link>,
                  ]}
                />
              ) : !token ? (
                <Result
                  status="error"
                  title={t('auth:resetPassword.linkInvalid')}
                  subTitle={
                    <div>
                      <p className="mb-2">{errorMessage}</p>
                      <p className="text-sm text-gray-500">
                        {CONTACT_CONFIG.serviceHotline}
                      </p>
                    </div>
                  }
                  extra={[
                    <Link to="/auth/forgot-password" key="forgot">
                      <Button
                        type="primary"
                        size="large"
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        {t('auth:activate.resend')}
                      </Button>
                    </Link>,
                    <Link to="/auth/login" key="login">
                      <Button size="large">
                        {t('auth:resetPassword.backToLogin')}
                      </Button>
                    </Link>,
                  ]}
                />
              ) : (
                <>
                  {errorMessage && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-600 text-sm">{errorMessage}</p>
                    </div>
                  )}

                  <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    size="large"
                  >
                    <Form.Item
                      name="password"
                      label={
                        <span className="text-gray-700 font-medium">
                          {t('auth:resetPassword.newPassword.label')}
                        </span>
                      }
                      rules={[
                        {
                          required: true,
                          message: t('auth:resetPassword.newPassword.required'),
                        },
                        {
                          min: 8,
                          message: t('validation:password.minLength', {
                            min: 8,
                          }),
                        },
                        {
                          validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const { checks } = checkPasswordStrength(value);
                            const missingRules: string[] = [];
                            if (!checks.uppercase) {
                              missingRules.push(
                                t('auth:resetPassword.requirements.uppercase')
                              );
                            }
                            if (!checks.lowercase) {
                              missingRules.push(
                                t('auth:resetPassword.requirements.lowercase')
                              );
                            }
                            if (!checks.number) {
                              missingRules.push(
                                t('auth:resetPassword.requirements.number')
                              );
                            }
                            if (!checks.special) {
                              missingRules.push(
                                t('auth:resetPassword.requirements.special')
                              );
                            }
                            if (missingRules.length > 0) {
                              return Promise.reject(
                                new Error(
                                  t(
                                    'auth:resetPassword.validation.requirementsMissing',
                                    {
                                      items: missingRules.join(
                                        t('auth:resetPassword.validation.separator')
                                      ),
                                    }
                                  )
                                )
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input.Password
                        prefix={<LockOutlined className="text-gray-400" />}
                        placeholder={t(
                          'auth:resetPassword.newPassword.placeholder'
                        )}
                        className="rounded-lg"
                        iconRender={visible =>
                          visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                        }
                        onChange={e => setPassword(e.target.value)}
                      />
                    </Form.Item>

                    {/* 密码强度指示器 */}
                    {password && (
                      <div className="mb-4 -mt-2">
                        <div className="flex items-center gap-2 mb-2">
                          <Progress
                            percent={strength}
                            showInfo={false}
                            strokeColor={getStrengthColor()}
                            size="small"
                            className="flex-1"
                          />
                          <span
                            className="text-sm font-medium"
                            style={{ color: getStrengthColor() }}
                          >
                            {getStrengthText()}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-1 text-xs">
                          <div
                            className={`flex items-center gap-1 ${
                              checks.length ? 'text-green-600' : 'text-gray-400'
                            }`}
                          >
                            {checks.length ? (
                              <CheckCircleFilled />
                            ) : (
                              <CloseCircleFilled />
                            )}
                            {t('auth:resetPassword.requirements.minLength')}
                          </div>
                          <div
                            className={`flex items-center gap-1 ${
                              checks.uppercase
                                ? 'text-green-600'
                                : 'text-gray-400'
                            }`}
                          >
                            {checks.uppercase ? (
                              <CheckCircleFilled />
                            ) : (
                              <CloseCircleFilled />
                            )}
                            {t('auth:resetPassword.requirements.uppercase')}
                          </div>
                          <div
                            className={`flex items-center gap-1 ${
                              checks.lowercase
                                ? 'text-green-600'
                                : 'text-gray-400'
                            }`}
                          >
                            {checks.lowercase ? (
                              <CheckCircleFilled />
                            ) : (
                              <CloseCircleFilled />
                            )}
                            {t('auth:resetPassword.requirements.lowercase')}
                          </div>
                          <div
                            className={`flex items-center gap-1 ${
                              checks.number ? 'text-green-600' : 'text-gray-400'
                            }`}
                          >
                            {checks.number ? (
                              <CheckCircleFilled />
                            ) : (
                              <CloseCircleFilled />
                            )}
                            {t('auth:resetPassword.requirements.number')}
                          </div>
                          <div
                            className={`flex items-center gap-1 ${
                              checks.special
                                ? 'text-green-600'
                                : 'text-gray-400'
                            }`}
                          >
                            {checks.special ? (
                              <CheckCircleFilled />
                            ) : (
                              <CloseCircleFilled />
                            )}
                            {t('auth:resetPassword.requirements.special')}
                          </div>
                        </div>
                      </div>
                    )}

                    <Form.Item
                      name="confirmPassword"
                      label={
                        <span className="text-gray-700 font-medium">
                          {t('auth:resetPassword.confirmPassword.label')}
                        </span>
                      }
                      dependencies={['password']}
                      rules={[
                        {
                          required: true,
                          message: t(
                            'auth:resetPassword.confirmPassword.required'
                          ),
                        },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue('password') === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error(
                                t('auth:resetPassword.confirmPassword.mismatch')
                              )
                            );
                          },
                        }),
                      ]}
                    >
                      <Input.Password
                        prefix={<LockOutlined className="text-gray-400" />}
                        placeholder={t(
                          'auth:resetPassword.confirmPassword.placeholder'
                        )}
                        className="rounded-lg"
                        iconRender={visible =>
                          visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
                        }
                      />
                    </Form.Item>

                    <Form.Item className="mb-4">
                      <Button
                        type="primary"
                        htmlType="submit"
                        block
                        loading={isLoading}
                        disabled={strength < 100}
                        className="h-11 rounded-lg font-medium bg-emerald-600 hover:bg-emerald-700 border-0"
                      >
                        {isLoading
                          ? t('auth:resetPassword.submitting')
                          : t('auth:resetPassword.submit')}
                      </Button>
                    </Form.Item>

                    <div className="text-center">
                      <Link
                        to="/auth/login"
                        className="text-emerald-600 hover:text-emerald-700"
                      >
                        {t('auth:resetPassword.backToLogin')}
                      </Link>
                    </div>
                  </Form>
                </>
              )}
            </div>
          </Card>

          {/* 版权信息 */}
          <div className="text-center mt-6">
            <p className="text-gray-400 text-sm">
              {t('common:app.copyright', { year: new Date().getFullYear() })}
            </p>
          </div>
        </div>
      </Col>
    </Row>
  );
};

export default ResetPasswordPage;
